import {
  type ChangeEvent,
  type InputHTMLAttributes,
  type JSX,
  type Ref,
  useCallback,
  useId,
  useState,
} from 'react';
import InputMask from '@mona-health/react-input-mask';
import { useTranslation } from 'react-i18next';
import { type Props as InputMaskProps } from 'react-input-mask';
import {
  getFormControlAriaProps,
  getFormControlErrorId,
  shouldShowError,
} from '@common/components/controls/ariaHelpers/formControlAriaHelpers';
import { DEFAULT_MASK_CHAR } from '@common/components/controls/constants';
import IconButton from '@common/components/controls/IconButton';
import { type RequiredTestId } from '@common/components/controls/TestId';
import IconEye from '@common/components/svgs/common/Eye';
import IconEyeCrossed from '@common/components/svgs/common/EyeCrossed';
import Translated from '@common/components/Translated';
import classnames from '@common/utils/ui/classnames';
import '@common/styles/components/inputField.pcss';

interface PasswordToggleProps {
  isPassword: boolean;
  disabled?: boolean;
  onToggle: () => void;
}

function PasswordToggleButton(
  { isPassword, disabled, onToggle }: Readonly<PasswordToggleProps>,
): JSX.Element {
  return (
    <IconButton
      data-testid={isPassword ? 'input-showPassword' : 'input-hidePassword'}
      icon={isPassword ? IconEye : IconEyeCrossed}
      disabled={disabled}
      onClick={onToggle}
    />
  );
}

interface InputStateClassParams {
  placeholder?: string;
  hasMask: boolean;
  maskedInputValue: string;
  value: unknown;
  focused: boolean;
  readOnly?: boolean;
  selecting?: boolean;
  disabled?: boolean;
  touched?: boolean;
  error?: string;
  fullWidth?: boolean;
  size?: number;
  dense?: boolean;
  contrast?: boolean;
}

const getInputFieldClasses = ({
  placeholder,
  hasMask,
  maskedInputValue,
  value,
  focused,
  readOnly,
  selecting,
  disabled,
  touched,
  error,
  fullWidth,
  size,
  dense,
  contrast,
}: InputStateClassParams) => {
  const hasValue = !!value || value === 0;
  const isActive = !!placeholder && (
    (hasMask && !!maskedInputValue)
    || hasValue
    || (focused && !readOnly)
    || selecting
  );

  return classnames({
    'input-field': true,
    'input-field--state-active': isActive,
    'input-field--state-focused': focused,
    'input-field--state-disabled': disabled,
    'input-field--state-error': touched && !!error,
    'input-field--full-width': fullWidth,
    'input-field--given-size': !!size && !fullWidth,
    'input-field--dense': dense,
    'input-field--contrast': contrast,
  });
};

export interface InputProps
  extends
    Omit<InputHTMLAttributes<HTMLInputElement>, 'children'>,
    Omit<Partial<InputMaskProps>, 'children'>
{
  tooltip?: string | JSX.Element;
  showError?: boolean;
  touched?: boolean;
  error?: string;
  fullWidth?: boolean;
  dense?: boolean;
  contrast?: boolean;
  selecting?: boolean;
  hideCaret?: boolean;
  startAdornment?: string | JSX.Element;
  endAdornment?: string | JSX.Element;
  inputRef?: Ref<HTMLInputElement>;
}

function Input({
  name,
  placeholder,
  disabled,
  readOnly,
  value,
  tooltip,
  touched,
  error,
  showError,
  onBlur,
  onFocus,
  fullWidth,
  size,
  dense,
  contrast,
  startAdornment,
  endAdornment,
  inputRef,
  selecting,
  hideCaret,
  mask,
  maskChar,
  type,
  'data-testid': testId,
  ...rest
}: RequiredTestId<InputProps>) {
  const [isFocused, setIsFocused] = useState(false);
  const [maskedInputValue, setMaskedInputValue] = useState('');
  const [fieldType, setFieldType] = useState(type);
  const { t } = useTranslation();
  const inputId = useId();
  const showsError = shouldShowError(error, touched, showError);
  const errorId = showsError ? getFormControlErrorId(inputId) : undefined;
  const tooltipId = tooltip && !showsError ? `${inputId}-tooltip` : undefined;

  const ariaProps = getFormControlAriaProps({
    invalid: showsError,
    errorId,
    disabled,
    ariaDescribedBy: tooltipId,
  });

  const tooltipContent = showsError && error
    ? <Translated id={error} anchor="validation-error" />
    : tooltip;

  const togglePasswordVisibility = useCallback(() => {
    setFieldType((current) => (current === 'password' ? 'text' : 'password'));
  }, []);

  const hasPasswordValue = type === 'password' && (!!value || value === 0);
  const endAdornmentContent = hasPasswordValue
    ? (
      <PasswordToggleButton
        isPassword={fieldType === 'password'}
        disabled={disabled}
        onToggle={togglePasswordVisibility}
      />
    )
    : endAdornment;

  const Content = (
    <label
      className={getInputFieldClasses({
        placeholder,
        hasMask: !!mask,
        maskedInputValue,
        value,
        focused: isFocused,
        readOnly,
        selecting,
        disabled,
        touched,
        error,
        fullWidth,
        size,
        dense,
        contrast,
      })}
    >
      <span className="input-field__wrapper">
        {!!startAdornment && (
          <span className="input-field__startAdornment">
            {startAdornment}
          </span>
        )}
        <span
          className="input-field__content"
          // For CSS auto-width
          data-name={placeholder
            ? t(placeholder, { defaultValue: mask ? placeholder : undefined })
            : undefined}
        >
          {placeholder && (
            <Translated
              id={placeholder}
              defaultMessage={mask ? placeholder : undefined}
              className="input-field__label"
            />
          )}
          <input
            ref={inputRef}
            id={inputId}
            {...ariaProps}
            className={classnames({
              'input-field__field': true,
              'no-select': readOnly || hideCaret,
              'hide-caret': hideCaret,
            })}
            name={name}
            onFocus={(e) => {
              setIsFocused(true);
              onFocus?.(e);
            }}
            onBlur={(e) => {
              setIsFocused(false);
              onBlur?.(e);
            }}
            value={value ?? ''}
            disabled={disabled}
            readOnly={readOnly}
            size={size}
            placeholder={dense && placeholder ? t(placeholder) : undefined}
            data-testid={testId}
            {...rest}
            type={fieldType}
          />
        </span>
        {!!endAdornmentContent && (
          <span className="input-field__endAdornment">
            {endAdornmentContent}
          </span>
        )}
      </span>
      {tooltipContent && (
        <span
          className="input-field__tooltip"
          id={showsError ? errorId : tooltipId}
          data-testid="inputTooltip"
        >
          {tooltipContent}
        </span>
      )}
    </label>
  );

  if (mask) {
    // Helper to check if masked value is empty (only contains placeholder characters)
    const isEmptyMaskedValue = (maskedValue: string): boolean => {
      const placeholderChar = maskChar ?? DEFAULT_MASK_CHAR;
      // If maskChar is null, we can't determine empty state reliably, treat as non-empty
      if (placeholderChar === null) {
        return false;
      }
      // Value is empty if it only contains the placeholder character or mask literals
      // (e.g., "__/__/____" for date mask "99/99/9999")
      // Common mask literals: / - ( ) space + (for international phone numbers)
      const maskLiterals = /[\s/\-+()]/;
      const hasOnlyPlaceholders = !maskedValue
        .split('')
        .some((char) => {
          // Character is actual content if:
          // 1. It's not the placeholder character
          // 2. It's not a common mask literal (/, -, +, (, ), space)
          // 3. Check for any Unicode letter or number using \p{L} and \p{N}
          //    This supports international characters (Arabic, Chinese, etc.)
          return char !== placeholderChar
            && !maskLiterals.test(char)
            && /[\p{L}\p{N}]/u.test(char);
        });
      return hasOnlyPlaceholders;
    };

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      // Set maskedInputValue to empty string if only placeholder characters remain
      setMaskedInputValue(isEmptyMaskedValue(newValue) ? '' : newValue);
    };

    return (
      <InputMask
        mask={mask}
        maskPlaceholder={maskChar === null ? null : (maskChar ?? DEFAULT_MASK_CHAR)}
        onChange={handleChange}
        value={`${value}`}
        {...rest}
      >
        {Content}
      </InputMask>
    );
  }

  return Content;
}

export default Input;
